import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  useAnalytics,
  useApplicationContext,
  useConfig,
} from '~/common/context';
import { ICustomerChangeEvent, ICustomerOption } from '~/common/types';
import { usePurchaseContext } from '~/purchase/context';

export function useCustomerChangeHandler() {
  const { sendEvent } = useAnalytics();
  const { auth } = useApplicationContext();
  const config = useConfig();
  const { handlers } = usePurchaseContext();
  const navigate = useNavigate();
  const location = useLocation();

  const updateNavigationOnCustomerChange = useCallback(() => {
    const PATTERNS_LOOKUP = [
      { pattern: /^(\/.*\/orders\/pending)(\/|$)/, redirect: '$1' },
      { pattern: /^(\/.*\/orders\/processed)(\/|$)/, redirect: '$1' },
      { pattern: /^(\/.*\/purchase\/orders)(\/|$)/, redirect: '$1' },
      { pattern: /^(\/pricing-policies\/quotes)(\/.*|$)/, redirect: '$1' },
      { pattern: /^(\/pricing-policies\/services)(\/.*|$)/, redirect: '$1' },
      // match only /quotes/:ref/edit, redirect to /quotes/history
      { pattern: /^(\/quotes\/.*\/edit)(\/|$)/, redirect: '/quotes/history' },
      { pattern: /^(\/purchase\/result)(\/|$)/, redirect: '/purchase' },
      {
        pattern: /^(\/quotes\/search\/result)(\/|$)/,
        redirect: '/quotes/search',
      },
    ];
    let match = null;
    let redirectPath = null;

    for (const { pattern, redirect } of PATTERNS_LOOKUP) {
      match = location.pathname.match(pattern);
      if (match) {
        redirectPath = redirect.replace('$1', match[1]);
        break;
      }
    }
    if (redirectPath && location.pathname !== redirectPath) {
      void navigate(redirectPath);
    }
  }, [location.pathname, navigate]);

  return useCallback(
    (selectedOption: ICustomerOption | null) => {
      sendEvent(auth.user, {
        type: 'customer_scope_change_event',
        customer: {
          id: selectedOption?.value,
          name: selectedOption?.name,
        },
        platformId: config.tenantId,
      } as ICustomerChangeEvent);
      handlers.setPrimaryCustomerId(selectedOption?.value);
      if (selectedOption?.value) {
        updateNavigationOnCustomerChange();
      }
    },
    [
      sendEvent,
      auth.user,
      config.tenantId,
      handlers,
      updateNavigationOnCustomerChange,
    ],
  );
}
